import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebaseClient';
import Icon from 'components/AppIcon';

const ActivityFeed = ({ itemId, itemType }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  // Load activity history from Firestore
  useEffect(() => {
    if (!itemId || !itemType) return;

    console.log('ActivityFeed loading for:', { itemId, itemType });

    const q = query(
      collection(db, 'edit_history'),
      where('item_id', '==', itemId),
      where('item_type', '==', itemType),
      orderBy('created_at', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('ActivityFeed snapshot received:', snapshot.docs.length, 'documents');
      const activityData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate() || new Date()
      }));
      console.log('ActivityFeed processed data:', activityData);
      setActivities(activityData);
      setLoading(false);
    }, (error) => {
      console.error('ActivityFeed query error:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [itemId, itemType]);

  const getFieldDisplayName = (field) => {
    const fieldNames = {
      'assignment_title': 'Assignment Title',
      'class_title': 'Class Title',
      'subject': 'Subject',
      'assignment_type': 'Assignment Type',
      'pages': 'Pages',
      'deadline': 'Deadline',
      'status': 'Status',
      'requirements': 'Requirements',
      'additional_notes': 'Additional Notes',
      'qa_review_notes': 'QA Review Notes',
      'preferred_communication': 'Communication Type',
      'assigned_writer': 'Assigned Writer',
      'class_forum': 'Class Forum',
      'start_date': 'Start Date',
      'end_date': 'End Date',
      'description': 'Description',
      'notes': 'Notes',
      'meeting_link': 'Meeting Link'
    };
    return fieldNames[field] || field;
  };

  const getActivityMessage = (activity) => {
    const fieldName = getFieldDisplayName(activity.field);
    const editorName = activity.editor_name || 'Unknown User';
    
    if (!activity.old_value && activity.new_value) {
      return `${editorName} added ${fieldName}`;
    }
    if (activity.old_value && !activity.new_value) {
      return `${editorName} removed ${fieldName}`;
    }
    if (activity.field === 'status') {
      return `${editorName} changed status from "${activity.old_value}" to "${activity.new_value}"`;
    }
    return `${editorName} updated ${fieldName}`;
  };

  const getActivityIcon = (activity) => {
    if (activity.field === 'status') return 'ArrowRight';
    if (!activity.old_value && activity.new_value) return 'Plus';
    if (activity.old_value && !activity.new_value) return 'Minus';
    return 'Edit';
  };

  const getActivityColor = (activity) => {
    if (activity.field === 'status') return 'text-blue-400';
    if (!activity.old_value && activity.new_value) return 'text-green-400';
    if (activity.old_value && !activity.new_value) return 'text-red-400';
    return 'text-yellow-400';
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'text-purple-400';
      case 'writer': return 'text-green-400';
      case 'qa': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  const formatValue = (value) => {
    if (!value) return 'Empty';
    if (typeof value === 'string' && value.length > 30) {
      return `${value.substring(0, 30)}...`;
    }
    return value.toString();
  };

  if (loading) {
    return (
      <div className="glass-card p-4 rounded-lg">
        <div className="flex items-center space-x-2">
          <Icon name="Activity" size={20} className="text-white/60" />
          <span className="text-white/60">Loading activity...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card p-4 rounded-lg space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Icon name="Activity" size={20} className="text-white/80" />
          <h3 className="text-white font-medium">Activity Feed</h3>
          <span className="text-white/60 text-sm">({activities.length})</span>
        </div>
        
        {activities.length > 0 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center space-x-1 text-white/60 hover:text-white/80 transition-colors"
          >
            <span className="text-sm">{expanded ? 'Show Less' : 'Show All'}</span>
            <Icon 
              name={expanded ? 'ChevronUp' : 'ChevronDown'} 
              size={16} 
            />
          </button>
        )}
      </div>

      {/* Activity List */}
      <div className="space-y-3">
        <AnimatePresence>
          {activities.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="Activity" size={48} className="text-white/20 mx-auto mb-2" />
              <p className="text-white/60">No activity yet</p>
              <p className="text-white/40 text-sm">Changes and updates will appear here</p>
            </div>
          ) : (
            activities.slice(0, expanded ? activities.length : 5).map((activity) => (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-white/5 rounded-lg p-4 border border-white/10 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                    <Icon 
                      name={getActivityIcon(activity)} 
                      size={16} 
                      className={getActivityColor(activity)} 
                    />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-white font-medium">
                        {getActivityMessage(activity)}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full bg-white/10 ${getRoleColor(activity.editor_role)}`}>
                        {activity.editor_role || 'user'}
                      </span>
                    </div>
                    
                    <div className="text-xs text-white/60 mb-2">
                      {format(activity.created_at, 'PPp')}
                    </div>
                    
                    {/* Show old/new values for updates */}
                    {activity.field !== 'status' && activity.old_value && activity.new_value && (
                      <div className="bg-white/5 rounded p-2 space-y-1">
                        <div className="text-xs">
                          <span className="text-red-400">Before:</span>
                          <span className="text-white/60 ml-2">{formatValue(activity.old_value)}</span>
                        </div>
                        <div className="text-xs">
                          <span className="text-green-400">After:</span>
                          <span className="text-white/80 ml-2">{formatValue(activity.new_value)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
        
        {!expanded && activities.length > 5 && (
          <button
            onClick={() => setExpanded(true)}
            className="w-full text-center py-2 text-white/60 hover:text-white/80 transition-colors text-sm border border-white/10 rounded-lg hover:bg-white/5"
          >
            Show {activities.length - 5} more activities...
          </button>
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;